import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Save, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import type { AdminRole } from '@/features/admin/types';
import { useCreateRole, useUpdateRole } from '@/features/admin/useAdmin';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role?: AdminRole | null;
}

const SUGGESTED_PERMS = [
  'users.read',
  'users.write',
  'users.delete',
  'roles.read',
  'roles.write',
  'roles.delete',
  'audit.read',
  'modules.calc.read',
  'modules.calc.write',
  'normas.read',
  'modules.all',
];

export function RoleFormDialog({ open, onOpenChange, role }: Props) {
  const isEdit = !!role;
  const create = useCreateRole();
  const update = useUpdateRole();
  const submitting = create.isPending || update.isPending;

  const [slug, setSlug] = useState('');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [permissions, setPermissions] = useState<string[]>([]);
  const [newPerm, setNewPerm] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (role) {
      setSlug(role.slug);
      setLabel(role.label);
      setDescription(role.description);
      setPermissions(role.permissions);
    } else {
      setSlug('');
      setLabel('');
      setDescription('');
      setPermissions([]);
    }
    setNewPerm('');
  }, [open, role]);

  const togglePerm = (p: string) => {
    setPermissions((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  };
  const addCustom = () => {
    const p = newPerm.trim();
    if (!p) return;
    if (!permissions.includes(p)) setPermissions((prev) => [...prev, p]);
    setNewPerm('');
  };
  const remove = (p: string) => setPermissions((prev) => prev.filter((x) => x !== p));

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      if (isEdit && role) {
        await update.mutateAsync({ id: role.id, data: { label, description, permissions } });
      } else {
        await create.mutateAsync({ slug, label, description, permissions });
      }
      onOpenChange(false);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'No se pudo guardar.');
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50" style={{ background: 'rgba(2,6,23,0.75)', backdropFilter: 'blur(8px)' }} />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-[var(--radius-lg)] border shadow-2xl"
          style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
        >
          <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex items-center justify-between border-b px-5 py-3" style={{ borderColor: 'var(--border-subtle)' }}>
              <Dialog.Title className="text-base" style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>
                {isEdit ? `Editar rol "${role?.slug}"` : 'Nuevo rol'}
              </Dialog.Title>
              <Dialog.Close asChild>
                <button type="button" aria-label="Cerrar" className="flex h-8 w-8 items-center justify-center rounded-[var(--radius-sm)] hover:bg-[var(--surface-2)]" style={{ color: 'var(--text-secondary)' }}>
                  <X size={14} />
                </button>
              </Dialog.Close>
            </div>

            <div className="scrollbar-thin flex-1 space-y-3 overflow-y-auto px-5 py-4">
              <Field label="Slug">
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.currentTarget.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
                  required
                  disabled={isEdit}
                  pattern="^[a-z][a-z0-9_-]*$"
                  className="h-9 w-full rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-3 font-mono tabular text-sm focus:border-[var(--accent)] focus:outline-none disabled:opacity-60"
                  style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
                />
              </Field>
              <Field label="Etiqueta">
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.currentTarget.value)}
                  required
                  className="h-9 w-full rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-3 text-sm focus:border-[var(--accent)] focus:outline-none"
                  style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
                />
              </Field>
              <Field label="Descripcion">
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.currentTarget.value)}
                  rows={2}
                  className="w-full rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-3 py-2 text-sm focus:border-[var(--accent)] focus:outline-none"
                  style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
                />
              </Field>

              <div>
                <span className="mb-1 block text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                  Permisos
                </span>
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {permissions.map((p) => (
                    <span
                      key={p}
                      className="inline-flex items-center gap-1 rounded-[var(--radius-pill)] border px-2 py-0.5 font-mono tabular text-[10px]"
                      style={{
                        background: 'rgba(245,158,11,0.10)',
                        borderColor: 'rgba(245,158,11,0.30)',
                        color: 'var(--accent-soft)',
                      }}
                    >
                      {p}
                      <button type="button" onClick={() => remove(p)} className="opacity-70 hover:opacity-100" aria-label={`Quitar ${p}`}>
                        <Trash2 size={10} strokeWidth={2} />
                      </button>
                    </span>
                  ))}
                  {permissions.length === 0 && (
                    <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                      Sin permisos seleccionados.
                    </span>
                  )}
                </div>
                <div className="mb-2 flex flex-wrap gap-1">
                  {SUGGESTED_PERMS.filter((p) => !permissions.includes(p)).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => togglePerm(p)}
                      className="rounded-[var(--radius-sm)] border px-2 py-0.5 font-mono tabular text-[10px] hover:bg-[var(--surface-2)]"
                      style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}
                    >
                      + {p}
                    </button>
                  ))}
                </div>
                <div className="flex gap-1">
                  <input
                    type="text"
                    value={newPerm}
                    onChange={(e) => setNewPerm(e.currentTarget.value)}
                    placeholder="custom.permission"
                    className="h-7 flex-1 rounded-[var(--radius-sm)] border bg-[var(--surface-2)] px-2 font-mono tabular text-[11px] focus:border-[var(--accent)] focus:outline-none"
                    style={{ borderColor: 'var(--border-default)', color: 'var(--text-primary)' }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustom();
                      }
                    }}
                  />
                  <button type="button" onClick={addCustom} className="flex h-7 items-center gap-1 rounded-[var(--radius-sm)] border px-2 text-[11px] hover:bg-[var(--surface-2)]" style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)' }}>
                    <Plus size={11} strokeWidth={2} /> Agregar
                  </button>
                </div>
              </div>

              {error && (
                <div className="rounded-[var(--radius-sm)] border px-3 py-2 text-xs" style={{ background: 'rgba(239,68,68,0.10)', borderColor: 'var(--danger)', color: 'var(--danger)' }}>
                  {error}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t px-5 py-3" style={{ borderColor: 'var(--border-subtle)' }}>
              <Dialog.Close asChild>
                <Button type="button" variant="ghost" size="sm">
                  Cancelar
                </Button>
              </Dialog.Close>
              <Button type="submit" variant="primary" size="sm" disabled={submitting}>
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} strokeWidth={1.8} />}
                {isEdit ? 'Guardar' : 'Crear rol'}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
        {label}
      </span>
      {children}
    </label>
  );
}
